import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ListScaffold } from "@/components/layout/ListScaffold";
import {
  Badge,
  PageHeader,
  Table,
  TableWrap,
  TD,
  TH,
  THead,
  TR,
} from "@/components/ui";
import { useApi } from "@/lib/api";
import { count, date, percent } from "@/lib/format";

/**
 * Cities, ordered by how much of each one is confirmed rather than by name.
 *
 * The distinction the table exists to make is COVERED versus LIVE. The OSM
 * import gives us thousands of candidate venues in a city for nothing;
 * launching one means people can actually find matcha there, and the gap
 * between the two columns is the work.
 */
export function CitiesScreen() {
  const api = useApi();
  const navigate = useNavigate();
  const cities = useQuery({
    queryKey: ["admin", "cities"],
    queryFn: async () => {
      const { data, error } = await api.GET("/admin/cities");
      if (error) throw error;
      return data;
    },
  });

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        eyebrow="Overview"
        title="Cities"
        description="Covered means the import ran there. Live means somebody can use the app there."
      />
      <ListScaffold
        isLoading={cities.isLoading}
        isError={cities.isError}
        onRetry={() => void cities.refetch()}
        isEmpty={(cities.data?.items.length ?? 0) === 0}
        emptyTitle="No cities yet"
        emptyHint="The OSM import creates a city the first time it runs against one."
      >
        <TableWrap>
          <Table>
            <THead>
              <TR>
                <TH>City</TH>
                <TH>Country</TH>
                <TH align="right">Cafes</TH>
                <TH align="right">Open</TH>
                <TH align="right">Confirmed</TH>
                <TH align="right">Coverage</TH>
                <TH>Launched</TH>
              </TR>
            </THead>
            <tbody>
              {cities.data?.items.map((city) => (
                <TR
                  key={city.id}
                  onClick={() => navigate(`/cafes?city=${city.id}`)}
                >
                  <TD>
                    <span className="font-medium text-ink">{city.name}</span>
                    <span className="text-muted-2"> · {city.slug}</span>
                  </TD>
                  <TD muted mono>
                    {city.country}
                  </TD>
                  <TD align="right" mono>
                    {count(city.cafeCount)}
                  </TD>
                  <TD align="right" mono>
                    {count(city.openCafeCount)}
                  </TD>
                  <TD align="right" mono>
                    {count(city.confirmedCount)}
                  </TD>
                  <TD align="right" mono muted>
                    {percent(
                      city.cafeCount > 0
                        ? (city.confirmedCount / city.cafeCount) * 100
                        : 0,
                    )}
                  </TD>
                  <TD>
                    {city.launchedAt ? (
                      <span className="text-muted">
                        {date(city.launchedAt)}
                      </span>
                    ) : (
                      <Badge tone="neutral">not live</Badge>
                    )}
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      </ListScaffold>
    </div>
  );
}
